import React, { useCallback, useEffect, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import styled from 'styled-components';
import {
  Avatar,
  Button,
  Heading,
  Tooltip as SuperopsTooltip,
  TextStyle,
  Badge,
  Popover,
  ActionList,
  Banner,
  DisplayText,
  TextField,
  Modal,
  ButtonGroup,
  Page,
  Dropdown
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import envConfig from '@nile/home/src/Config/env';
import {
  getTranslatedText,
  COLOR_MAP,
  TEXT_LENGTH,
  COMMON_CONSTANTS,
  reactSelectErrorStyles,
  VariableInput,
  Generator,
  formatGroupLabel,
  formatDataForSelectDropdown,
  RunbookNodeActivatorContent,
  useProduct,
  withErrorBoundary
} from '@nile/components';
import { convertMinutesToHours } from '@nile/mangifera/src/Utils/helpers';
import DurationDropdown from '@nile/components/src/Dropdowns/DurationDropdown';
import { injectIntl, FormattedMessage } from 'react-intl';
import AssociateTasks from '@nile/mangifera/src/Containers/ChatContainers/AssociateTasks';
import { GET_ASSET_SCRIPT_LIST_V2 } from '@nile/rmm/src/modules/scripts/Graphql';
import {
  AccountClient,
  GET_SERVICE_ITEMS,
  GET_ELIGIBLE_SERVICE_ITEMS
} from '@nile/graphql';
import { useLazyQuery, useQuery } from '@apollo/client';
import { orderBy, forEach, cloneDeep, set, pick, isEmpty } from 'lodash';
import {
  DropdownIndicator,
  IndicatorSeparator
} from '@nile/components/src/Pebbles/select';
import Select from 'react-select';
import gql from 'graphql-tag';
import { Tooltip } from 'react-clone-tippy';

const DataGetter = styled.div`
  .value_selector__control {
    background: none !important;
    border-bottom: none !important;
  }
  .Polaris-Connected,
  .Superops-Connected {
    padding-top: 0 !important;
  }
  .Polaris-TextField,
  .Superops-TextField {
    background: transparent;
  }
  .Polaris-TextField__Input,
  .Superops-TextField__Input {
    padding-top: 5px;
    padding-bottom: 0;
    border-bottom: 0 !important;
    min-height: 70px;
    max-height: 200px;
    overflow: auto;
  }
  .Polaris-TextField--multiline > .Polaris-TextField__Input,
  .Superops-TextField--multiline > .Superops-TextField__Input {
    position: relative;
    // top: -10px;
  }
`;

const IconHolder = styled.div`
  padding: ${(props) => props.padding || '5px'};
  background: ${(props) => (props.selected ? '#fff4ce' : '#fff')};
  height: ${(props) => props.size || '32px'};
  width: ${(props) => props.size || '32px'};
  cursor: pointer;
  transition: background 200ms linear;

  svg {
    height: ${(props) => props.iconsize || '20px'};
    width: ${(props) => props.iconsize || '20px'};
  }
`;

const defaultOptions = {
  page: 1,
  sort: [
    {
      attribute: 'name',
      order: 'ASC'
    }
  ],
  condition: null
};

function AddWorklogModal(props) {
  const { intl, isAutoAction } = props;

  const saveLoading = false;

  const formData = props?.autoActionformData || props?.data?.formData || {};
  const actionValueData =
    props?.autoActionformData?.actionValueData || formData?.actionValueData;

  const parentActionId = formData?.parentActionId;
  const hasActionId = formData?.actionId;
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);

  const [popoverActive, setPopoverActive] = useState(
    Boolean(
      (isAutoAction && !props?.autoActionformData) ||
        (!isAutoAction && !hasActionId)
    )
  );

  const showDeleteButton =
    (isAutoAction && props?.autoActionformData) ||
    (!isAutoAction && hasActionId);

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((active) => !active);
  }, []);

  const existingWorklog = actionValueData?.worklog;

  const [selectedItem, setSelectedItem] = useState(
    existingWorklog?.serviceItem || null
  );
  const [comments, setComments] = useState(existingWorklog?.notes || '');
  const [timespent, setTimespent] = useState(
    existingWorklog?.timeSpent * 60 || 15
  );
  const [billable, setBillable] = useState(existingWorklog?.billable || false);

  function convertTimeToDecimal(timeString) {
    // Extract hours and minutes using regex
    const hoursMatch = timeString.match(/(\d+)\s*h/);
    const minutesMatch = timeString.match(/(\d+)\s*m/);

    // Parse hours and minutes (default to 0 if not found)
    const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;

    // Convert minutes to a fraction of an hour
    const decimal = hours + minutes / 60;

    // Round to 2 decimal places for readability
    return Math.round(decimal * 100) / 100;
  }

  const onSave = () => {
    const formDataTemp = {
      name: formData?.name || '', // name returned in response.
      // id: props.id,
      actionType: 'SINGLE',
      actionProcessCategory: props?.isAutoAction ? 'ASYNC' : 'SYNC',
      actionValueType: 'WORKLOG',
      actionValueData: {
        nodeId: props.id,
        worklog: {
          serviceItem: {
            name: selectedItem?.name,
            itemId: selectedItem?.itemId
          },
          billable: billable,
          notes: comments,
          type: 'MANUAL',
          timeSpent: convertTimeToDecimal(
            convertMinutesToHours(Number(timespent))
          )
        }
      }
    };

    if (hasActionId) {
      formDataTemp.actionId = hasActionId;
    }
    if (parentActionId) {
      formDataTemp.parentActionId = parentActionId;
    }

    if (props.isAutoAction) {
      props.autoActionSave(formDataTemp);
    } else {
      props.modifyNodeData(formDataTemp);
    }
    togglePopoverActive();
  };

  const activator = (
    <RunbookNodeActivatorContent
      nodeType="admin.runbooks.templateactions.addworklog"
      nodeName={selectedItem?.name || ''}
      iconName="WorklogColoured"
      callBackFunction={togglePopoverActive}
    />
  );

  const {
    loading: itemsForTimeTrackingLoading,
    data: itemsForTimeTracking,
    error: itemsForTimeTrackingError
  } = useQuery(GET_ELIGIBLE_SERVICE_ITEMS, {
    client: AccountClient,
    variables: {
      input: {
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              // {
              //   attribute: 'useAsWorklogItem',
              //   operator: 'is',
              //   value: true
              // },
              {
                attribute: 'serviceTypeItem.offeringType',
                operator: 'is',
                value: 'HOURS'
              }
            ]
          }
        }
      }
    }
  });

  const options = itemsForTimeTracking
    ? formatDataForSelectDropdown(
        itemsForTimeTracking.getEligibleServiceCatalogItems?.items?.filter(
          (item) => item.status === 'ACTIVE'
        )
      )
    : [];

  useEffect(() => {
    if (itemsForTimeTracking) {
      const [
        foundTemplate
      ] = itemsForTimeTracking.getEligibleServiceCatalogItems?.items.filter(
        (item) =>
          item.status === 'ACTIVE' &&
          item.itemId === actionValueData.worklog?.serviceItem?.itemId
      );
      if (foundTemplate) {
        setSelectedItem(foundTemplate);
      }
    }
  }, [itemsForTimeTracking, popoverActive]);

  useEffect(() => {
    if (popoverActive) {
      if (existingWorklog?.serviceItem) {
        setSelectedItem(existingWorklog?.serviceItem);
      }
      setComments(existingWorklog?.notes || '');
      setTimespent(existingWorklog?.timeSpent * 60 || 15);
      setBillable(existingWorklog?.billable || false);
    }
  }, [popoverActive, existingWorklog]);

  const handleNodeDelete = () => {
    setPopoverActive(false);

    if (props.isAutoAction) {
      props.handleAutoActionDelete(props.id);
    } else {
      props.data.handleNodeDelete(props.id, hasActionId);
    }
  };

  const handleNodeCancel = useCallback(() => {
    setPopoverActive(false);
    if (!props.isAutoAction && !hasActionId) {
      handleNodeDelete();
    } else if (props.isAutoAction && !props?.autoActionformData) {
      props.handleAutoActionDelete(props.id);
    }
  }, [hasActionId, handleNodeDelete]);

  return (
    <Modal
      medium
      activator={activator}
      open={popoverActive}
      onClose={togglePopoverActive}
      showCloseButton={false}
      noScroll
    >
      <Page
        title={getTranslatedText(
          intl,
          'admin.runbooks.templateactions.addworklog'
        )}
        contentHeight="110px"
        fullWidth
        variant="secondary"
        primaryAction={
          <ButtonGroup spacing="loose">
            {showDeleteButton && (
              <Button plainSecondary onClick={handleNodeDelete}>
                <div className="danger">
                  <FormattedMessage id="project.details.delete" />
                </div>
              </Button>
            )}
            <Button plainSecondary onClick={handleNodeCancel}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              icon={<SVGIcon name="Save" />}
              onClick={onSave}
              disabled={
                (isMSPProduct && isEmpty(selectedItem)) || isEmpty(comments)
              }
              loading={saveLoading}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        }
      >
        <div className="p20 pl-40">
          {isMSPProduct && (
            <div className="flex center w50 mb-10">
              <SVGIcon name="Cart" />
              <DataGetter className="full-width ml-10 pl10 br-5 timer-bg border">
                <Dropdown
                  className=""
                  classNamePrefix="value_selector"
                  formatGroupLabel={formatGroupLabel}
                  components={{ IndicatorSeparator, DropdownIndicator }}
                  title={getTranslatedText(
                    intl,
                    'admin.timeTracking.chooseDefaultTimerService'
                  )}
                  onChange={(val) => {
                    const selectedValue = {
                      ...pick(val, ['itemId', 'name'])
                    };
                    setSelectedItem(selectedValue);
                    // handlePropertyChange('serviceItem', selectedValue);
                  }}
                  options={options}
                  placeholder={getTranslatedText(
                    intl,
                    'admin.timeTracking.chooseDefaultTimerService.placeholder'
                  )}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  closeMenuOnScroll={(e) => {
                    return (
                      e.target &&
                      e.target.classList &&
                      !(e.target.classList.value.indexOf('menu-list') > -1)
                    );
                  }}
                  value={
                    selectedItem
                      ? {
                          ...selectedItem,
                          label: selectedItem.name,
                          value: selectedItem.name
                        }
                      : null
                  }
                  name="valueselector"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 })
                  }}
                  noOptionsMessage={() => {
                    return itemsForTimeTrackingLoading
                      ? 'Loading...'
                      : 'No options';
                  }}
                  isClearable={false}
                />
              </DataGetter>
            </div>
          )}
          <div className="flex center full-width">
            <div className="min-width-20 flex center">
              <SVGIcon name="Notes" />
            </div>
            <DataGetter className="timer-bg full-width ml-10 pl10 br-5 border">
              <TextField
                placeholder={getTranslatedText(
                  intl,
                  'workItem.details.add.note'
                )}
                multiline
                value={comments}
                onChange={(val) => {
                  setComments(val);
                }}
                maxLength={TEXT_LENGTH.size_9}
                // disabled={!workItem && !selectedItem}
                // error={errors?.notes?.[0]}
                // autoFocus={autoFocusTimerNotes}
              />
            </DataGetter>
          </div>
          <div className="flex center w30 mb-30">
            <div className="min-width-20 flex center">
              <SVGIcon name="EstimatedTime" />
            </div>
            <div className="ml-10 mt-10 timer-bg br-5 border">
              <DurationDropdown
                // date={segmentDate}
                duration={timespent}
                // endTime={segmentEndTime}
                // startTime={segmentStartTime}
                onChange={(val) => {
                  setTimespent(val);
                }}
              />
            </div>
          </div>

          <div>
            <Tooltip
              title={getTranslatedText(
                intl,
                billable
                  ? 'worklog.details.markedAsBillable'
                  : 'worklog.details.markAsBillable'
              )}
              position="top-start"
              inertia
              className="full-width"
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <IconHolder
                selected={billable}
                className="border-black br-5"
                onClick={() => {
                  setBillable(!billable);
                }}
                padding="3px"
                size="24px"
                iconsize="16px"
              >
                <SVGIcon name="Billable" />
              </IconHolder>
            </Tooltip>
          </div>
        </div>
      </Page>
    </Modal>
  );
}

export default withErrorBoundary(injectIntl(AddWorklogModal));
